<template>
  <div>
    <RequestOtpStep
      v-if="step === STEP.EMAIL"
      v-model="email"
      :loading="isSubmitting"
      :error-message="errorMessage"
      @submit="sendOtp" />

    <VerifyOtpStep
      v-else-if="step === STEP.CODE"
      v-model="otp"
      :email="email"
      :resend-countdown="resendCountdown"
      :verifying="isVerifyingOtp"
      @resend="sendOtp"
      @change-email="restartWithNewEmail"
      @submit="goToPasswordStep" />

    <NewPasswordStep
      v-else
      :password="password"
      :confirm-password="confirmPassword"
      :loading="isSubmitting"
      :error-message="errorMessage"
      @update:password="password = $event"
      @update:confirmPassword="confirmPassword = $event"
      @submit="resetPassword" />
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import RequestOtpStep from './forgot-password/RequestOtpStep.vue';
  import VerifyOtpStep from './forgot-password/VerifyOtpStep.vue';
  import NewPasswordStep from './forgot-password/NewPasswordStep.vue';
  import { requestResetOtp, submitNewPassword } from '@/utils/authApi.js';

  const STEP = {
    EMAIL: 'email',
    CODE: 'code',
    PASSWORD: 'password'
  };

  const RESEND_COOLDOWN_SECONDS = 60;
  /* the API only checks the OTP together with the new password, so the step change
     gets a short verifying state — jumping instantly reads as if nothing happened */
  const OTP_CHECK_FEEDBACK_MS = 700;

  export default {
    name: 'ForgotPasswordView',
    components: {
      RequestOtpStep,
      VerifyOtpStep,
      NewPasswordStep
    },
    data() {
      return {
        STEP: STEP,
        step: STEP.EMAIL,
        email: '',
        otp: '',
        password: '',
        confirmPassword: '',
        errorMessage: '',
        isSubmitting: false,
        isVerifyingOtp: false,
        resendCountdown: 0,
        countdownTimer: null,
        otpCheckTimer: null
      }
    },
    computed: {
      ...mapGetters(['getForgotPassword'])
    },
    watch: {
      step(value) {
        this.$emit('back-state', value !== STEP.EMAIL);
      }
    },
    /* a pending reset survives a reload through the persisted store */
    created() {
      if (this.getForgotPassword) {
        this.email = this.getForgotPassword;
        this.step = STEP.CODE;
      }
    },
    beforeDestroy() {
      this.stopCountdown();
      if (this.otpCheckTimer) clearTimeout(this.otpCheckTimer);
    },
    methods: {
      ...mapActions(['setForgotPassword']),
      startCountdown() {
        this.stopCountdown();
        this.resendCountdown = RESEND_COOLDOWN_SECONDS;
        this.countdownTimer = setInterval(() => {
          this.resendCountdown -= 1;
          if (this.resendCountdown <= 0) this.stopCountdown();
        }, 1000);
      },
      stopCountdown() {
        if (!this.countdownTimer) return;
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.resendCountdown = 0;
      },
      restartWithNewEmail() {
        this.stopCountdown();
        this.isVerifyingOtp = false;
        this.setForgotPassword('');
        this.otp = '';
        this.step = STEP.EMAIL;
      },
      goBack() {
        if (this.step === STEP.PASSWORD) {
          this.step = STEP.CODE;
          return;
        }
        if (this.step === STEP.CODE) this.restartWithNewEmail();
      },
      sendOtp() {
        if (this.isSubmitting || this.resendCountdown > 0) return;

        this.isSubmitting = true;
        this.errorMessage = '';

        requestResetOtp(this.email).then(result => {
          if (!result.isSuccess) {
            this.errorMessage = result.message || this.$t('profile.auth.generic-error');
            return;
          }
          this.setForgotPassword(this.email);
          this.step = STEP.CODE;
          this.startCountdown();
        }).catch(() => {
          this.errorMessage = this.$t('profile.auth.generic-error');
        }).then(() => {
          this.isSubmitting = false;
        });
      },
      goToPasswordStep() {
        if (this.isVerifyingOtp) return;

        this.errorMessage = '';
        this.isVerifyingOtp = true;
        this.otpCheckTimer = setTimeout(() => {
          this.isVerifyingOtp = false;
          this.step = STEP.PASSWORD;
        }, OTP_CHECK_FEEDBACK_MS);
      },
      resetPassword() {
        if (this.isSubmitting) return;

        this.isSubmitting = true;
        this.errorMessage = '';

        submitNewPassword({
          email: this.email,
          otp: this.otp,
          password: this.password,
          confirmPassword: this.confirmPassword
        }).then(result => {
          if (!result.isSuccess) {
            this.errorMessage = result.message || this.$t('profile.auth.generic-error');
            return;
          }
          this.setForgotPassword('');
          this.$emit('flash', this.$t('profile.auth.reset-success'));
          this.$emit('switch-view', 'login');
        }).catch(() => {
          this.errorMessage = this.$t('profile.auth.generic-error');
        }).then(() => {
          this.isSubmitting = false;
        });
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/v2/auth/views/ForgotPasswordView.vue